/* eslint-disable @atlassian/relay/unused-fields */
import { useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type { ApolloClient, ApolloError } from 'apollo-client';
import { useQuery, type QueryHookOptions } from '@apollo/react-hooks';
import type { Extension, GQLExtension } from '../graphql/types';
import {
	type GQLExtensionContext,
	type GQLExtensionContextExtensionsByTypeVariables,
	type GQLExtensionContextsVariables,
	isGQLGatewayError,
	isGQLUnderlyingServiceError,
	isRelayNetworkError,
} from '../graphql/types';

import { type ContextId } from '@atlassian/forge-ui-types';
import { useMetricsContext } from '../../context';
import { getExtensionListQuery, getExtensionListQueryOld } from './lib/queries';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
import { createAtlAttributionHeader } from '../../utils';
import {
	type ExtensionContextsFilter,
	type useExtensionList_forge_ui_Query as extensionListQuery,
} from './__generated__/useExtensionList_forge_ui_Query.graphql';

export interface QueryVariables {
	contextIds: GQLExtensionContextsVariables['contextIds'];
	filter?: ExtensionContextsFilter[];
	locale?: string;
	type?: GQLExtensionContextExtensionsByTypeVariables['type'];
}

export interface QueryData {
	extensionContexts: GQLExtensionContext[];
}

export type UseExtensionQueryHookOptions = QueryHookOptions<QueryData, QueryVariables>;

export interface PrefilteredExtensionsResult {
	confluence_forgeExtensionsByType?: GQLExtension[];
}
export interface PreFilteredExtensionsVariables {
	cloudId: string;
	context: GQLExtensionRenderingContextInput;
	includeHidden: boolean;
	locale?: string;
	types: string[];
}

export type UsePreFilteredExtensionsQueryOptions = PreFilteredExtensionsVariables & {
	client?: ApolloClient<object>;
	queryOptions?: UsePreFilteredExtensionsQueryOptions;
};

export type PreFilteredExtensionsQueryHookOptions = QueryHookOptions<
	PrefilteredExtensionsResult,
	PreFilteredExtensionsVariables
>;

export type GQLExtensionRenderingContextInput = {
	contentId?: string;
	spaceId?: string;
	spaceKey?: string;
};

export type UseExtensionListOptions = QueryVariables & {
	client?: ApolloClient<object>;
	dataClassificationTagIds?: string[];
	expandAppOwner?: boolean;
	queryOptions?: UseExtensionQueryHookOptions;
};

interface CommonHookResult {
	error?: ApolloError;
	loading: boolean;
}

export interface UseExtensionListHookResult extends CommonHookResult {
	extensions: GQLExtensionContext['extensions'];
}

export type UseExtensionListRelayHookResult = Omit<UseExtensionListHookResult, 'error'> & {
	error?: Error;
};

export interface UsePreFilteredExtensionListHookResult extends CommonHookResult {
	extensions: Extension[];
}

export interface ExtensionListGQLResult<T> {
	data?:
		| { extensionContexts?: Array<{ extensions?: T[] }> | Array<{ extensionsByType?: T[] }> }
		| extensionListQuery['response'];
	loading: boolean;
}

export const createExtensionListQueryOptions = <T>(
	client: ApolloClient<object> | undefined,
	contextIds: ContextId[],
	queryOptions?: T,
	expandAppOwner?: boolean,
	filter?: ExtensionContextsFilter[],
	extensionType?: string,
	dataClassificationTagIds?: string[],
	locale?: string,
	ssr: boolean = false,
): T => {
	const variables = filter
		? { contextIds, locale, filter }
		: { contextIds, locale, type: extensionType };
	const query = filter
		? getExtensionListQuery(expandAppOwner, dataClassificationTagIds)
		: getExtensionListQueryOld(expandAppOwner, undefined, dataClassificationTagIds);

	// In SSR mode render and fetch is one-pass we can't do cache-and-network
	if (ssr && (queryOptions as any)?.fetchPolicy === 'cache-and-network') {
		(queryOptions as any).fetchPolicy = 'cache-first';
	}

	return {
		variables,
		...queryOptions,
		query,
		client,
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
			},
		},
	} as unknown as T;
};

export const getExtensionsFromGQLResult = <T>({
	loading,
	data,
}: ExtensionListGQLResult<T>): T[] | null => {
	if (!loading && data && data.extensionContexts && data.extensionContexts[0]) {
		if ('extensions' in data.extensionContexts[0]) {
			// @ts-ignore
			return data.extensionContexts[0].extensions ?? null;
		} else if ('extensionsByType' in data.extensionContexts[0]) {
			return data.extensionContexts[0].extensionsByType ?? null;
		}
	}

	return null;
};

export const useExtensionList = ({
	contextIds,
	type: extensionType,
	filter,
	client,
	queryOptions,
	expandAppOwner,
	dataClassificationTagIds,
	locale,
}: UseExtensionListOptions): UseExtensionListHookResult => {
	const requestOptions = createExtensionListQueryOptions<UseExtensionQueryHookOptions>(
		client,
		contextIds,
		queryOptions,
		expandAppOwner,
		filter,
		extensionType,
		dataClassificationTagIds,
		locale,
	);
	const query = filter
		? getExtensionListQuery(expandAppOwner)
		: getExtensionListQueryOld(expandAppOwner);

	const { error, loading, data } = useQuery<QueryData, QueryVariables>(
		requestOptions.query || query,
		requestOptions,
	);
	const extensions = getExtensionsFromGQLResult({ loading, data });

	const { page } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (extensions) {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'succeeded',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'useExtensionList',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, page, createAnalyticsEvent]);

	useEffect(() => {
		if (
			// if hydration of appOwners (non-critical data) fails but we still have the extensions,
			// we shouldn't count this as a bad event
			!extensions &&
			error &&
			error.graphQLErrors.length > 0
		) {
			const err = error.graphQLErrors.find(
				(err) => isGQLUnderlyingServiceError(err) || isGQLGatewayError(err),
			);

			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'useExtensionList',
						errorName: err
							? isGQLUnderlyingServiceError(err)
								? 'componentApi'
								: 'componentGraphQL'
							: 'uncaught',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, error, page, createAnalyticsEvent]);

	return {
		error,
		extensions: extensions || [],
		loading,
	};
};

export const useExtensionListRelay = ({
	contextIds,
	type: extensionType,
	filter: extensionFilter,
	dataClassificationTagIds,
	locale,
}: UseExtensionListOptions): UseExtensionListRelayHookResult => {
	const [extensions, setExtensions] = useState<GQLExtension[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const { page } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const environment = useRelayEnvironment();
	// Context ids only ever contains a single id.
	// Extract out the id so that the useEffect dependency is a string instead of an array
	const contextId = contextIds[0];

	useEffect(() => {
		const filter = extensionFilter ?? [
			{
				type: 'EXTENSION_TYPE' as const,
				value: [extensionType] as readonly string[],
			},
		];
		const contextIds = [contextId];

		fetchQuery<extensionListQuery>(
			environment,
			graphql`
				query useExtensionList_forge_ui_Query(
					$contextIds: [ID!]!
					$filter: [ExtensionContextsFilter!]!
					$locale: String
					$dataClassificationTags: [ID!]!
				) {
					extensionContexts(contextIds: $contextIds) {
						id
						extensions(filter: $filter, locale: $locale) @optIn(to: "Access-Narrowing") {
							id
							appId
							key
							appOwner {
								name
							}
							environmentId
							environmentType
							environmentKey
							properties
							license {
								active
								type
								supportEntitlementNumber
								trialEndDate
								subscriptionEndDate
								isEvaluation
								billingPeriod
								ccpEntitlementId
								ccpEntitlementSlug
								capabilitySet
								modes
							}
							userAccess {
								enabled
								hasAccess
							}
							type
							appVersion
							installationId
							migrationKey
							installationConfig {
								key
								value
							}
							egress {
								type
								addresses
								category
								inScopeEUD
							}
							scopes
							consentUrl
							currentUserConsent {
								user {
									aaid
								}
								appEnvironmentVersion {
									id
								}
								consentedAt
							}
							requiresUserConsent
							dataClassificationPolicyDecision(
								input: { dataClassificationTags: $dataClassificationTags }
							) {
								status
							}
						}
					}
				}
			`,
			{
				contextIds,
				locale,
				filter,
				dataClassificationTags: dataClassificationTagIds || [],
			},
			{
				networkCacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
						},
					},
				},
			},
		).subscribe({
			next: (data) => {
				const extensions = getExtensionsFromGQLResult<GQLExtension>({ data, loading: false });

				if (extensions) {
					createAnalyticsEvent({
						eventType: OPERATIONAL_EVENT_TYPE,
						data: {
							action: 'succeeded',
							actionSubject: 'forge.ui.webClient',
							source: page,
							tags: ['forge'],
							attributes: {
								target: 'useExtensionList',
							},
						},
					}).fire(FORGE_UI_ANALYTICS_CHANNEL);
				}

				setExtensions(extensions);
				setLoading(false);
			},
			error: (error: Error) => {
				setError(error);
				setLoading(false);
			},
		});
	}, [
		contextId,
		extensionFilter,
		extensionType,
		locale,
		dataClassificationTagIds,
		environment,
		page,
		createAnalyticsEvent,
	]);

	useEffect(() => {
		if (
			// if hydration of appOwners (non-critical data) fails but we still have the extensions,
			// we shouldn't count this as a bad event
			!extensions &&
			error &&
			isRelayNetworkError(error)
		) {
			const err = error.source.errors.find(
				(err) => isGQLUnderlyingServiceError(err) || isGQLGatewayError(err),
			);

			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'useExtensionList',
						errorName: err
							? isGQLUnderlyingServiceError(err)
								? 'componentApi'
								: 'componentGraphQL'
							: 'uncaught',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, error, page, createAnalyticsEvent]);

	return {
		extensions: extensions || [],
		loading,
		error,
	};
};
